import { isProd, serializeError } from '@w/utils'

export const PREPARE_FOR_LOGS_DEFAULT_OPTIONS = {
  maxStringLength: 100,
  maxArrayLength: 10,
  maxObjectKeyCount: 80,

  isSensitive: (_key: string, _value: unknown) => true,
}

function formatMaskedSuffix(value: unknown): string {
  if (
    value === null ||
    typeof value === 'object' ||
    typeof value === 'function'
  )
    return '[Object]'
  if (typeof value === 'string') return value
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'number') return value.toString()
  if (typeof value === 'bigint') return value.toString()
  if (typeof value === 'symbol') return value.toString()
  return 'undefined'
}

export type PayloadLimits = Pick<
  typeof PREPARE_FOR_LOGS_DEFAULT_OPTIONS,
  'maxStringLength' | 'maxArrayLength' | 'maxObjectKeyCount'
>

type PrepareForLogOptions = Partial<typeof PREPARE_FOR_LOGS_DEFAULT_OPTIONS> & {
  maskEnabled?: boolean
  removeMark?: boolean
}

/**
 * Prepares data for logging by handling errors, truncating, and masking sensitive information
 * @param obj - The input data to prepare
 * @param opts - Options for error handling, truncation, and masking
 * @returns The prepared data, with errors handled, truncated, and masked
 */
export function prepareForLog(
  obj: unknown,
  opts?: Partial<PrepareForLogOptions>,
): unknown {
  try {
    const options: PrepareForLogOptions = {
      ...PREPARE_FOR_LOGS_DEFAULT_OPTIONS,
      maskEnabled: isProd,
      ...opts,
    }

    if (obj instanceof Response) return obj

    if (obj === undefined) return undefined
    if (obj === null) return null
    if (obj instanceof Date) return obj

    // Handle UFile instances by logging UFileInfo
    if (
      obj &&
      typeof obj === 'object' &&
      'size' in obj &&
      'path' in obj &&
      'blob' in obj
    ) {
      const ufile = obj as a
      return {
        size: ufile.size,
        path: ufile.path,
        type: ufile.type,
        ...(ufile.meta ? { meta: ufile.meta } : {}),
      }
    }

    // Handle UFileStream instances by logging UFileInfo
    if (
      obj &&
      typeof obj === 'object' &&
      'info' in obj &&
      'stream' in obj &&
      obj.info &&
      typeof obj.info === 'object' &&
      'size' in obj.info &&
      'path' in obj.info
    ) {
      const info = obj.info as a
      return {
        size: info.size,
        path: info.path,
        type: info.type,
        ...(info.meta ? { meta: info.meta } : {}),
      }
    }

    if (typeof obj === 'function') return '[Function]'
    if (isBinary(obj)) return binarySize(obj)
    if (typeof obj === 'symbol') return String(obj)
    if (typeof obj !== 'object') return obj // this is not a good solution because we may log sensitive data
    // throw new Error('Primitives in prepareForLog not supported. please return objects or null')

    if (obj instanceof Error)
      throw new Error(
        'Error in prepareForLog not supported. please use prepareTelError instead',
      )

    // @ts-expect-error TS2339: __preparedForLog is a runtime marker not in the type
    if (obj.__preparedForLog) {
      // @ts-expect-error TS2339: __preparedForLog is a runtime marker not in the type
      if (options.removeMark) delete obj.__preparedForLog
      return obj
    }

    const truncatedData = truncate(obj, options)
    const masked = maskSensitiveData(truncatedData, options)
    if (options.removeMark) return masked
    return { __preparedForLog: true, ...masked }
  } catch (unexpectedError) {
    return {
      message: `Unexpected error in prepareForLog: ${unexpectedError instanceof Error ? unexpectedError.message : String(unexpectedError)}`,
      data: typeof obj === 'object' ? 'Object' : typeof obj,
      __preparedForLog: true,
    }
  }
}

export function prepareInputForLog(
  input: unknown,
  limits: Partial<PayloadLimits> = {},
): unknown {
  const options = { ...PREPARE_FOR_LOGS_DEFAULT_OPTIONS, ...limits }
  const seen = new WeakSet<object>()
  // Bound total traversal as well as each container, including cyclic inputs.
  let remaining = 1 + options.maxObjectKeyCount * (1 + options.maxArrayLength)
  return truncate(input, options, value => {
    if (--remaining < 0) return '[Payload limit]'
    if (isBinary(value)) return binarySize(value)
    if (value && typeof value === 'object') {
      if (seen.has(value)) return '[Circular]'
      seen.add(value)
    }
    return value
  })
}

function isBinary(value: unknown): value is ArrayBufferView | ArrayBuffer {
  return ArrayBuffer.isView(value) || value instanceof ArrayBuffer
}

function binarySize(value: ArrayBufferView | ArrayBuffer) {
  return `<${value.byteLength} bytes>`
}

/**
 * Truncates strings, arrays, and object keys based on provided options
 */
function truncate<T>(
  input: T,
  options: Partial<PrepareForLogOptions> = {},
  transform?: (value: unknown) => unknown,
  ancestors = new Set<object>(),
): T {
  try {
    if (transform) {
      const transformed = transform(input)
      if (transformed !== input) return transformed as T
    }
    if (typeof input === 'function') return '[Function]' as T
    if (typeof input === 'symbol') return String(input) as T
    // Object.keys on a typed array allocates one key per element: a 5 MB page cost ~1 s per span.
    if (isBinary(input)) return binarySize(input) as T
    if (input && typeof input === 'object') {
      if (ancestors.has(input)) return '[Circular]' as T
      ancestors = new Set(ancestors).add(input)
    }
    // Extract truncation options
    const maxStringLength =
      options.maxStringLength ??
      PREPARE_FOR_LOGS_DEFAULT_OPTIONS.maxStringLength
    const maxArrayLength =
      options.maxArrayLength ?? PREPARE_FOR_LOGS_DEFAULT_OPTIONS.maxArrayLength
    const maxObjectKeyCount =
      options.maxObjectKeyCount ??
      PREPARE_FOR_LOGS_DEFAULT_OPTIONS.maxObjectKeyCount

    // Handle null or undefined
    if (input === null || input === undefined) {
      return input
    }

    // Handle Date objects
    if (input instanceof Date) {
      return input
    }

    // An Error has no own enumerable keys: without this, `tel.warn(msg, { error })` exported `error={}`.
    if (input instanceof Error) {
      return truncate(serializeError(input), options, transform, ancestors) as T
    }

    // Handle UFile instances
    if (
      !transform &&
      input &&
      typeof input === 'object' &&
      'size' in input &&
      'path' in input &&
      'blob' in input
    ) {
      const ufile = input as a
      return {
        size: ufile.size,
        path: ufile.path,
        type: ufile.type,
        ...(ufile.meta ? { meta: ufile.meta } : {}),
      } as unknown as T
    }

    // Handle UFileStream instances
    if (
      !transform &&
      input &&
      typeof input === 'object' &&
      'info' in input &&
      'stream' in input &&
      input.info &&
      typeof input.info === 'object' &&
      'size' in input.info &&
      'path' in input.info
    ) {
      const info = input.info as a
      return {
        size: info.size,
        path: info.path,
        type: info.type,
        ...(info.meta ? { meta: info.meta } : {}),
      } as unknown as T
    }

    // Handle primitives (number, boolean)
    if (typeof input !== 'object' && typeof input !== 'string') {
      return input
    }

    // Handle strings
    if (typeof input === 'string') {
      if (input.length <= maxStringLength) {
        return input
      }
      return `${input.slice(0, maxStringLength)}...` as unknown as T
    }

    // Handle arrays
    if (Array.isArray(input)) {
      if (input.length <= maxArrayLength) {
        return (input as unknown[]).map((item) =>
          truncate(item, options, transform, ancestors),
        ) as unknown as T
      }
      const truncated: unknown[] = (input as unknown[])
        .slice(0, maxArrayLength)
        .map((item) => truncate(item, options, transform, ancestors))
      truncated.push(`... (${input.length - maxArrayLength} more items)`)
      return truncated as unknown as T
    }

    // Handle objects
    const keys = Object.keys(input)
    const result = {} as Record<string, unknown>

    const keysToProcess = keys.slice(0, maxObjectKeyCount)

    for (const key of keysToProcess) {
      try {
        // Report getters opaquely to avoid side effects while logging.
        const descriptor = Object.getOwnPropertyDescriptor(input, key)
        if (descriptor?.get) {
          result[key] = '[Getter]'
          continue
        }
        result[key] = truncate((input as Record<string, unknown>)[key], options, transform, ancestors)
      } catch (keyError) {
        result[key] =
          `[Error processing key: ${keyError instanceof Error ? keyError.message : String(keyError)}]`
      }
    }

    if (keys.length > maxObjectKeyCount) {
      result['...'] = `(${keys.length - maxObjectKeyCount} more keys)`
    }

    return result as unknown as T
  } catch (truncateError) {
    // If truncation fails, return a simplified version of the input
    const errorResult = {
      truncateError: `Error truncating data: ${truncateError instanceof Error ? truncateError.message : String(truncateError)}`,
      type: typeof input,
      isArray: Array.isArray(input),
    }
    return errorResult as unknown as T
  }
}

interface MaskOptions {
  isSensitive?: (key: string, value: unknown) => boolean
  maskEnabled?: boolean
}

function maskSensitiveData<T>(input: T, options: MaskOptions): T {
  const { isSensitive = () => true, maskEnabled = true } = options

  const maskValue = (value: unknown, sensitive: boolean): unknown => {
    if (value === null || value === undefined) return value
    if (typeof value === 'string' && value.trim() === '') return value
    if (sensitive)
      return maskEnabled ? '***' : `***${formatMaskedSuffix(value)}`
    return value
  }

  if (input === null || input === undefined) return input
  if (input instanceof Date) return input

  if (typeof input !== 'object') {
    return maskValue(input, isSensitive('', input)) as T
  }

  if (Array.isArray(input)) {
    return (input as unknown[]).map((item) =>
      maskSensitiveData(item, options),
    ) as unknown as T
  }

  const result = {} as Record<string, unknown>

  for (const [key, value] of Object.entries(input)) {
    if (key === 'tel') continue

    const sensitive = isSensitive(key, value)

    if (typeof value !== 'object' || value === null) {
      result[key] = maskValue(value, sensitive)
    } else if (Array.isArray(value)) {
      result[key] = maskSensitiveData(value, options)
    } else {
      result[key] = maskSensitiveData(value, options)
    }
  }

  return result as unknown as T
}

interface FileLike {
  size: unknown
  path: unknown
  type?: unknown
  meta?: unknown
}
type a = FileLike
